import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { transactionKeys } from '@/lib/query-keys';
import { financialService } from '@/services/financial.service';
import { useTransactionFilterStore } from '@/stores/transactionFilterStore';

export function useTransactions() {
  const queryClient = useQueryClient();
  const filters = useTransactionFilterStore();

  // datePreset drives the toolbar dropdown but is not an API field — the backend
  // rejects unknown query params (forbidNonWhitelisted), so strip it from every
  // request. dateFrom/dateTo (which it computes) are what actually go over.
  const toApiParams = (f: typeof filters.current) => {
    const rest: Record<string, unknown> = { ...f };
    delete rest.datePreset;
    return rest;
  };

  const transactionsQuery = useQuery({
    queryKey: transactionKeys.list(filters.current),
    queryFn: () => financialService.getTransactions(toApiParams(filters.current)),
    placeholderData: keepPreviousData,
  });

  const createTransaction = useMutation({
    mutationFn: (data: Parameters<typeof financialService.createTransaction>[0]) => financialService.createTransaction(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: transactionKeys.lists() }); toast.success('Transaction created'); },
    onError: () => toast.error('Failed to create transaction'),
  });

  return {
    transactions: transactionsQuery.data?.data ?? [],
    meta: transactionsQuery.data?.meta,
    isLoading: transactionsQuery.isLoading,
    filters,
    createTransaction,
    exportData: (format: string, override?: Record<string, unknown>) =>
      financialService.exportTransactions({ ...(override ?? toApiParams(filters.current)), format }),
  };
}
